import React, { useState } from "react";
import { isString } from "underscore";
import ReactDomServer from 'react-dom/server';

import EditableTextDraftQuill from "../../../general-ui/editable-text/editable-text-draft-quill";
import EditableTextSlim from "../../../general-ui/editable-text/editable-text-slim";
import Icon, { ICONS, COLORS } from "../../../general-ui/icon";
import { EUploadOptions, ImageUploader, ImageUploadOptions } from "../../../general-ui/image-uploader/image-uploader";
import { OptionalComponent } from "../../../../utils/optional-component";
import { InitialAspectRatioOptionsEnum } from "../../../../types/working-model";
import { regCodePlaceholder } from "./event-emails";


export type emailContentTypes = 'session' | 'event';

interface Props {
	onEdit: any;
	design: any;
	emailContent: any;
	emailContentType: emailContentTypes;
	editableContent: any;
	customContent: any;
	buttonText?: string;
	sent?: boolean;
}

interface Return<T> {
	getCellContent: (key: keyof T) => JSX.Element;
	getButton: () => JSX.Element;
	getImage: () => JSX.Element;
	EditImageModal: JSX.Element;
}

function useEditableEmailFields<T>(props: Props): Return<T> {
	const {
		onEdit,
		design,
		emailContentType,
		emailContent,
		editableContent,
		customContent,
		buttonText,
		sent,
	} = props;

	const [imageModalOpen, setImageModalOpen] = useState<ImageUploadOptions>(null);

	const getCellContent = (key: keyof T) => {
		if (onEdit && !sent) {
			const stringValue = customContent?.[key] ?? ReactDomServer.renderToString(editableContent[key]);
			if (!isString(stringValue)) return;

			return (
				<EditableTextDraftQuill
					useNew
					value={stringValue}
					onChange={(v) => onEdit(v, key)}
					disableAnchorTargets
				/>
			);
		}
		const jsxValue = customContent?.[key] ? <div dangerouslySetInnerHTML={{ __html: customContent[key] as string }} /> : editableContent[key];
		return jsxValue;
	};

	const getButton = (): JSX.Element => {
		const buttonValue = customContent?.button ?? buttonText ?? '';

		if (onEdit && isString(buttonValue) && !sent) {
			return (
				<div style={design.styles.button} className="email-button-editor-wrapper">
					<EditableTextSlim value={buttonValue} onChange={(v) => onEdit(v, 'button')} tagType="p" maxCharacters={65} displayCountAtXCharacter={55} />
				</div>
			);
		}

		// all emails get the placeholder so if the recipient is a registered attendee they will be taken straight into the event
		// the email sender removes the placeholder for unregistered attendees
		const url = `${emailContent[emailContentType]?.url}${regCodePlaceholder}`;

		return (
			<a href={url} data-saferedirecturl={emailContent[emailContentType]?.url} target="_blank" rel="noreferrer" style={design.styles.button} tabIndex={sent ? -1 : undefined}>
				{buttonValue}
			</a>
		);
	};

	const getImage = (): JSX.Element => {
		if (isString(customContent?.primary_image)) return <></>;
		const imageUrl = customContent?.primary_image?.edited ?? customContent?.primary_image?.original ?? emailContent[emailContentType]?.image;

		const isSessionEmail = emailContentType === 'session';
		const imageAspectRatio = isSessionEmail ? '56.25%' : '30%';
		const imageMargins = isSessionEmail ? '20px 0' : '0 0 20px';

		// height placeholder if there is no image
		if (!imageUrl) {
			return (
				<td align="center">
					<span>&nbsp;</span>
				</td>
			);
		}

		if (onEdit) {
			return (
				<td
					align="center"
					className="primary-image-editor-wrapper"
					style={{
						...design.styles.primaryImage,
						// paddingTop is what is handling the aspect ratio. (16:9 = 9/16 = .5625)
						paddingTop: imageAspectRatio,
						margin: imageMargins,
						backgroundImage: `url(${imageUrl})`
					}}
				>
					<div className="edit-button-container">
						{customContent?.primary_image?.original ? (
							<button className="round edit-button" onClick={() => setImageModalOpen(EUploadOptions.edit)}><Icon name={ICONS.IMAGE_CROP} color={COLORS.WHITE} size={14} /></button>
						) : null}
						<button className="round edit-button replace-image" onClick={() => setImageModalOpen(EUploadOptions.upload)}>
							<Icon name={ICONS.IMAGE} color={COLORS.GRAY} size={16} />
						</button>
					</div>
				</td>
			);
		}

		return (
			<td align="center" style={{ ...design.styles.primaryImage, backgroundImage: `url(${imageUrl})`, paddingTop: imageAspectRatio, margin: imageMargins, }} />
		);
	};

	const EditImageModal = (
		<OptionalComponent display={!!onEdit}>
			{isString(customContent?.primary_image) ? <></> :
				<ImageUploader
					originalImage={customContent?.primary_image?.original}
					disableEditingAspectRatio
					initialAspectRatio={InitialAspectRatioOptionsEnum[emailContentType === 'session' ? '16:9' : '10:3']}
					onSave={(image) => onEdit?.(image, 'primary_image')}
					onClose={() => setImageModalOpen(null)}
					open={imageModalOpen}
				/>
			}
		</OptionalComponent>
	);

	return {
		getCellContent,
		getButton,
		getImage,
		EditImageModal
	};
}

export default useEditableEmailFields;